import { useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../../containers/layout/app";
import { AddJournalEntryWrapper } from "./styled";
import { AppContext } from "../../../../context/appContext";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../../assets";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Typography } from "@mui/material";
import Cookies from "universal-cookie";
import { addJournalEntryService } from "../../../../utils/api/accounting/journalentry/addJournalEntry";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllChartOfAccountService } from "../../../../utils/api/accounting/chartofaccount/retrieveAllChartOfAccount";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseButton } from "../../../../components/button/styled";

export const AddJournalEntry = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { setActiveNavItem } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [formDetails, setFormDetails] = useState({
        debitGL: " ",
        creditGL: " ",
        debitAmount: "",
        creditAmount: "",
        narration: "",
    });

    useEffect(() => {
        setActiveNavItem("Accounting");
    }, [setActiveNavItem]);

    const { data: glAccounts } = useQuery({
        queryKey: ["manualEntriesAllowedGLAccounts", TOKEN],
        queryFn: async () => {
            const response = await retrieveAllChartOfAccountService(TOKEN);
            return response?.data?.filter((gl: Record<string, any>) => gl.glAccountType === "GL Account" && gl.allowManualEntries === "yes") ?? [];
        },
        enabled: !!TOKEN,
    });

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const response = await addJournalEntryService(TOKEN, formDetails);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Failed to add journal entry. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to add journal entry. ${error.message}`);
            console.error('Failed to add journal entry:', error);
        }
    };

    return (
        <AppLayout
            pageId={"Journal-Entry"}
            pageTitle={"Add Journal Entry"}
        >
            <AddJournalEntryWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title="Journal Entry Successful"
                    message="Your information has been successfully added to the system."
                    callToAction="Back to Journal Entry"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter Required Information to add journal entry.
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    GL to Debit
                                </BaseLabel>
                                <BaseSelect
                                    name="debitGL"
                                    fontweight={400}
                                    value={formDetails.debitGL}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select GL
                                    </BaseOption>
                                    {glAccounts?.map((gl: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={gl.id}
                                            >
                                                {gl.glName?.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    GL to Credit
                                </BaseLabel>
                                <BaseSelect
                                    name="creditGL"
                                    fontweight={400}
                                    value={formDetails.creditGL}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select GL
                                    </BaseOption>
                                    {glAccounts?.map((gl: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={gl.id}
                                            >
                                                {gl.glName?.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Debit Amount
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="debitAmount"
                                    value={formDetails.debitAmount}
                                    placeholder="Enter Debit Amount"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Credit Amount
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="creditAmount"
                                    value={formDetails.creditAmount}
                                    placeholder="Enter Credit Amount"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Narration
                                </BaseLabel>
                                <BaseInput
                                    required
                                    multiline
                                    minRows={4}
                                    name="narration"
                                    value={formDetails.narration}
                                    placeholder="Add any additional details or notes."
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Submit
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </AddJournalEntryWrapper>
        </AppLayout>
    )
}